import * as React from "react"
import { Container, Row, Col, Table, Button, Spinner } from "react-bootstrap"
import { Subscription, BehaviorSubject, of, concat, Observable } from "rxjs"
import { distinctUntilChanged, switchMap, map, catchError, filter } from "rxjs/operators"
import { toast } from "react-toastify"
import { isCompanyAdmin, isPortalAdmin, isSiteAdmin } from "../../authentication/isAdminRole"
import { AppState } from "../../AppState"
import { connect } from "react-redux"
import { APIError } from "../../api/ObservableFromFetch"
import { Typeahead } from "react-bootstrap-typeahead"
import { FaAngleDown } from "react-icons/fa"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import ColumnHeaderFilter from "../ColumnHeaderFilter"
import TablePagination from "../TablePagination"
import { CustomizableFilterColumn } from "../ColumnsCustomizeModal/CustomizableColumn"
import UserDetailsObservable, { ColumnFilters, UserDetailGridResponse, UserDetailsCSVObservable } from "../../api/Administration/GetUserDetails"
import UserDetail from "../../api/Administration/Model/UserDetail"
import UserInfo, { UserInfoMode } from "./UserInfo"
import FormatDate from "../../formatters/FormatDate"
import { Filter, Sort } from "../../api/GridRequest"
import ImplementationData from "../../api/Implementations/Model/ImplementationData"
import ImplementationsGetObservable from "../../api/Implementations/ImplementationsGet"
import SearchBar from "../SearchBar"
import SearchField from "../../api/SearchField"
import CSVUploadModal from "../Invitation/CSVUploadModal"
import { ApiRootUrl } from "../../api/ApiUrl"
import { UserUploadV2, UserUploadData } from "../../api/Invitation/InvitationCreate"
import { getUserNameForSelectedCompany, RoleIdForSelectedCompany } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import { IpifyUrl } from "../../config/Constants"
import UserProfileStatuses from "./UserProfileStatuses"
import downloadCsv from "../../assets/images/download-csv.png"
import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import FileSaver from "file-saver"
import { getStatusLabelByKey, InvitationStatus } from "../../api/Invitation/Model/InvitationStatus"

type Column = CustomizableFilterColumn<keyof UserDetail, keyof ColumnFilters>

interface UserDetailsGridProps {
  isSiteAdmin: boolean,
  isPortalAdmin: boolean,
  isCompanyAdmin: boolean,
  UserName?: string
  PrimaryRoleID?: number
}

interface UserDetailsGridState {
  loading: boolean
  loadAll: boolean
  loadingCsv?: boolean
  data?: UserDetailGridResponse
  pageNumber: number
  columns: Column[]
  sort?: Sort
  filters: Filter[]
  showUserInfoFor?: string,
  showNewUser: boolean,
  loginAttempts?: number
  selectedImplementation?: ImplementationData
  implementations?: ImplementationData[]
  participationId?: number
  roleId?: number
  locationId?: number
  timeZoneId?: number
  searchField: string
  searchFields?: SearchField[]
  searchText: string
  showUploadSpreadsheet: boolean
  currentIP: string
}

interface UserDetailSearchParams {
  pageNumber: number
  sort: Sort
  filters: Filter[]
}

const DEFAULT_COLUMNS: Column[] = [
  { name: "Account", filter: "Account", label: "Account", visible: true },
  { name: "LoginName", filter: "LoginName", label: "Login Name", visible: true },
  { name: "UserName", filter: "UserName", label: "User Name", visible: true },
  { name: "PrimaryRoleTitle", filter: "Role", label: "Role", visible: true },
  { name: "LastLoginDate", filter: "LastLogin", label: "Last Login", visible: true },
  { name: "ProfileStatuses", filter: "ProfileStatuses", label: "Profile Status", visible: false },
  { name: "SendingFacility", filter: "SendingFacility", label: "Sending Facility", visible: false },
  { name: "InActive", filter: "InActive", label: "Active/Inactive", visible: true },
  { name: "ModifiedOn", filter: "InvitationSent", label: "Invite Sent", visible: true },
  { name: "EnrollmentStarted", filter: "EnrollmentStarted", label: "Enrollment Started", visible: true},
  { name: "Status", filter: "Status", label: "Invite Status", visible: true },
  { name: "Link", label: "Invite Link", visible: false }


]

const SEARCH_FIELDS: SearchField[] = [
  { ID: "Login", Name: "Login Name" },
  { ID: "Name", Name: "User Name" }
]

const PageSize = 20
export class UserDetailsGrid extends React.PureComponent<UserDetailsGridProps, UserDetailsGridState> {
  state: UserDetailsGridState = {
    showUploadSpreadsheet: false,
    showNewUser: false,
    loading: false,
    loadAll: false,
    loadingCsv: false,
    pageNumber: 1,
    columns: DEFAULT_COLUMNS,
    filters: [],
    participationId: undefined,
    locationId: undefined,
    timeZoneId: undefined,
    searchField: "",
    searchText: "",
    searchFields: SEARCH_FIELDS,
    currentIP: ""
  }
  subscription?: Subscription
  implementationsSubscription?: Subscription
  exportToCSVSubscription?: Subscription

  implementationsTypeahead = React.createRef<Typeahead<ImplementationData>>()

  page$ = new BehaviorSubject<UserDetailSearchParams>({
    pageNumber: 1,
    sort: { column: "", order: "" },
    filters: []
  })

  componentDidMount() {
    this.setState({ columns: this.visibleGridColumns(["ProfileStatuses", "SendingFacility"], this.props.isSiteAdmin) })

    this.subscription = this.page$.pipe(
      distinctUntilChanged(),
      filter(() => this.state.loadAll || !this.props.isSiteAdmin || !!this.state.selectedImplementation?.ImplementationID || this.state.searchText.length > 0),
      switchMap(params => concat(
        of({ loading: true }),
        UserDetailsObservable(params.pageNumber, PageSize, params.sort, params.filters, this.state.selectedImplementation?.ImplementationID).pipe(
          map((result: UserDetailGridResponse) => ({ data: result, pageNumber: params.pageNumber, loading: false })),
          catchError(() => of({ loading: false }))
        )
      ))
    ).subscribe(
      (newState: { loading: boolean, data?: UserDetailGridResponse, pageNumber?: number }) => {
        if (newState.data) {
          if (newState.data.InActive?.length) {
            newState.data.InActive = newState.data.InActive.map(inactiveString => inactiveString === "true" ? "Inactive" : "Active")
          }
          if (newState.data.LastLogin?.length) {
            newState.data.LastLogin = newState.data.LastLogin.map(dateString => FormatDate(dateString))
          }
          this.setState(prevState => ({ loading: newState.loading, data: newState.data, pageNumber: newState.pageNumber || prevState.pageNumber }))
        } else {
          this.setState({ loading: newState.loading })
        }
      },
      (error: string) => {
        toast.error(error)
        this.setState({ loading: false })
      }
    )
    if (this.props.isSiteAdmin) {
      this.implementationsSubscription = ImplementationsGetObservable().subscribe(
        data => this.setState({ implementations: data.Records }),
        error => toast.error((error as APIError)?.errorData?.message || error.message)
      )
    }
  }

  visibleGridColumns(columns: string[], visible: boolean) {
    return this.state.columns.map(column => (columns.includes(column.name))
      ? { ...column, visible: visible }
      : column
    )
  }

  componentWillUnmount() {
    this.subscription && this.subscription.unsubscribe()
    this.exportToCSVSubscription && this.exportToCSVSubscription.unsubscribe()
    this.implementationsSubscription && this.implementationsSubscription.unsubscribe()
  }

  componentDidUpdate(prevProps: UserDetailsGridProps, prevState: UserDetailsGridState) {
    if (!!this.state.selectedImplementation && prevState.selectedImplementation?.ImplementationID !== this.state.selectedImplementation.ImplementationID) {
      this.page$.next({ pageNumber: 1, sort: { column: "", order: "" }, filters: [] })
    }
    else if (this.state.searchText !== prevState.searchText) {
      const searchField = this.state.searchField
      const searchText = this.state.searchText
      const filters = []
      if (searchField === "Login") { filters.push({ column: "LoginName", values: [searchText || ""] }) }
      if (searchField === "Name") { filters.push({ column: "UserName", values: [searchText || ""] }) }
      if (filters.length || this.state.selectedImplementation?.ImplementationID) {
        this.page$.next({ pageNumber: 1, sort: { column: "", order: "" }, filters: filters })
      }
      else {
        this.setState({ data: undefined })
      }
    }
  }

  UserIPGet = (): void => {
    try {
      const response = fetch(IpifyUrl).then(response => response.json())
      response.then(data => this.setState({ currentIP: data.ip }))
    } catch(error: unknown) {
      this.setState({ currentIP: "" })
    }
  }

  pageChanged = (pageNumber: number) => {
    this.page$.next({ ...this.page$.getValue(), pageNumber: pageNumber })
  }

  onExportClick() {
    this.setState({ loadingCsv: true })

    this.exportToCSVSubscription = UserDetailsCSVObservable(this.props.isSiteAdmin, 1, PageSize, this.state.sort || { column: "", order: "" }, this.state.filters, this.state.selectedImplementation?.ImplementationID).subscribe(
      (results: string) => {
        this.setState({ loadingCsv: false })
        this.exportToCSV(results)
      },
      (error) => {
        this.setState({ loadingCsv: false })
        toast.error(error)
      }
    )
  }

  exportToCSV(csv: string) {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName("OurTeam", "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }


  sort(column: string, order?: string) {
    const newSort = { column: column, order: order || "" }
    this.setState({ sort: newSort })
    this.page$.next({ ...this.page$.getValue(), sort: newSort })
  }

  filter(column: string, items: string[]) {
    this.setState(prevState => {
      const filters = [...prevState.filters]
      const filter = { column: column, values: items }
      let index = filters.findIndex(item => item.column === filter.column)
      if (index >= 0) {
        filters[index] = filter
      } else {
        filters.push(filter)
      }
      return { filters: filters }
    }, () => {
      this.page$.next({ ...this.page$.getValue(), pageNumber: 1, filters: this.state.filters })
    })
  }

  visibleColumns = () => this.state.columns.filter(col => col.visible)
  showTeamInfoModal = (data: UserDetail) => this.setState({ showUserInfoFor: data.LoginName, roleId: data.PrimaryRoleID, participationId: data.ParticipationID, locationId: data.LocationID, timeZoneId: data.TimeZoneID, loginAttempts: data.LoginAttempts })
  showNewTeamInfoModal = () => this.setState({ showUserInfoFor: undefined, showNewUser: true, participationId: undefined, timeZoneId: undefined })

  renderCell(row: UserDetail, colName: keyof UserDetail, key: number) {
    switch (colName) {
      case "LoginName":
        return <td key={key} className="align-text-top text-left p-0" onClick={() => this.showTeamInfoModal(row)}><Button variant="link">{row[colName]}</Button></td>
      case "InActive":
        return <td key={key} className="align-text-top">{row[colName] ? "Inactive" : "Active"}</td>
      case "LastLoginDate":
        return <td key={key} className="align-text-top">{FormatDate(row.LastLoginDate)}</td>
      case "ProfileStatuses":
        return <td key={key} className="align-text-top"><UserProfileStatuses statuses={row.ProfileStatuses} /></td>
        case "ModifiedOn":
          return <td key={key} className="align-text-top">{FormatDate(row.ModifiedOn)}</td>
        case "EnrollmentStarted":
          return <td key={key} className="align-text-top">{FormatDate(row.EnrollmentStarted)}</td>
        case "Status":
          return <td key={key} className="align-text-top">{getStatusLabelByKey(row.Status)}</td>
        case "Link":
          return <td key={key} className="align-text-top"><Button variant="link" onClick={() => this.onGoToInvitationLink(row.Link)} className="text-left p-0">{row.Link}</Button></td>
        default:
        return <td key={key} className="align-text-top">{row[colName]}</td>
    }
  }

  handleInvitationSetupModalClose = (hasUpdates: boolean) => {
    this.setState({ showUserInfoFor: undefined, showNewUser: false })
    if (hasUpdates) {
      this.pageChanged(1)
    }
  }

  handleImplementationChange = (selected: ImplementationData[]) => this.setState({ selectedImplementation: selected[0], searchField: "", searchText: "" })
  onSearch = (searchField?: string, searchText?: string) => this.setState({ searchField: searchField || "", searchText: searchText || "" })

  handleOpenUpload = () => this.setState({ showUploadSpreadsheet: true })

  handleStartUploading = (fileName: string, fileContent: string) => {
    const userData: UserUploadData = {
      Base64String: fileContent,
      IPAddress: this.state.currentIP,
      Link: window.location.hostname,
      ModifiedBy: this.props.UserName || "",
      RoleID: this.props.PrimaryRoleID || 0,
    }
    return UserUploadV2(userData)
  }

  handleBulkUploaded = () => {
    this.setState({ showUploadSpreadsheet: false })
  }

  LoadAll = () => this.setState({ loadAll: true }, () => this.page$.next({ pageNumber: 1, sort: { column: "", order: "" }, filters: [] }))

  onGoToInvitationLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  render() {
    const dataRows = (data: UserDetailGridResponse) => data.Records.map((row: UserDetail, i: number) => <tr key={i}>{dataColumns(row)}</tr>)
    const dataColumns = (row: UserDetail) => this.visibleColumns().map((col, i) => this.renderCell(row, col.name as keyof UserDetail, i))
    const emptyRow = <tr><td colSpan={this.visibleColumns().length}>NO DATA FOUND</td></tr>
    const siteAdminEmptyRow = <tr><td className="text-center" colSpan={this.visibleColumns().length}><Button variant="primary" onClick={this.LoadAll}>Load for All Portals</Button></td></tr>

    const headerColumns = this.visibleColumns().map((col: Column, i) => {
      return (
        <td key={i}>
          {this.state.data && col.filter && col.filter in this.state.data && <ColumnHeaderFilter title={col.label || col.name}
            filtered={this.state.filters.findIndex(item => item.column === col.filter) >= 0}
            sort={this.state.sort && this.state.sort.column === col.name ? this.state.sort.order === "asc" ? "asc" : "desc" : undefined}
            items={this.state.data[col.filter as keyof ColumnFilters] || []}
            uppercase={false}
            onSort={(order?: string) => this.sort(col.name, order)}
            onFilter={(items: string[]) => col.filter && this.filter(col.filter, items)} />}
          {(!this.state.data || !col.filter) && (col.label || col.name)}
        </td>
      )
    })

    return (<>
      <AppPageWrapper showNav parentClassName={"align-items-center"}>
        <Container fluid className="grid-container">
          {(this.props.isSiteAdmin) && <Row className="pb-2">
            <Col className="m-1" lg={3}>
              <SearchBar allowBlank searchFields={this.state.searchFields || []} onSearch={(searchField?: string, searchText?: string) => { this.onSearch(searchField, searchText) }} />
            </Col>
            {!this.state.loadAll && <Col className={"text-right"} lg={{span: 3, offset: 4}}>
              <Typeahead id="implementation"
                ref={this.implementationsTypeahead}
                labelKey="Name"
                filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                isLoading={!this.state.implementations}
                onChange={this.handleImplementationChange}
                options={this.state.implementations || []}
                selected={this.state.selectedImplementation ? [this.state.selectedImplementation] : []}
                emptyLabel="No Portals Found"
                placeholder="Type to select Portal"
                inputProps={{ autoComplete: "chrome-off" }}>
                {this.state.implementations &&
                  <Button variant="toggle" onClick={() => { this.implementationsTypeahead.current?.toggleMenu() }}>
                    <FaAngleDown />
                  </Button>}
              </Typeahead>
            </Col>}
            <Col className={"text-right"}>
              {this.state.loadAll && <Button variant="primary" size="sm" className="mr-2 mt-1 fixed-w5" onClick={() => this.setState({ loadAll: false, data: undefined })}>Show Portal Filter</Button>}
              {!this.state.loadAll && <Button variant="primary" size="sm" className="mr-2 mt-1 fixed-w5" onClick={() => this.setState({ loadAll: false, data: undefined, selectedImplementation: undefined })}>Reset Grid</Button>}
              <Button variant="secondary" size="sm" className="mt-1 mr-2" onClick={() => this.onExportClick()}>
                  EXPORT CSV&nbsp;
                  {this.state.loadingCsv ? <Spinner animation="border" size="sm" /> : <img src={downloadCsv} alt="" width="16" height="16" />}
              </Button>
            </Col>
          </Row>}
          {(this.props.isPortalAdmin || this.props.isCompanyAdmin) && <Row className="buttons-block">
            <Button variant="primary" size="sm" className="float-right mr-2 fixed-w5" onClick={this.handleOpenUpload}>Bulk Import Users</Button>
            <Button variant="primary" size="sm" className="float-right mr-2 fixed-w5" onClick={() => this.showNewTeamInfoModal()}>Add User</Button>
            <Button variant="secondary" size="sm" className="mt-1 mr-2" onClick={() => this.onExportClick()}>
                  EXPORT CSV&nbsp;
                  {this.state.loadingCsv ? <Spinner animation="border" size="sm" /> : <img src={downloadCsv} alt="" width="16" height="16" />}
              </Button>
          </Row>}
          <Row>
            <Col>
              <Table bordered responsive>
                <thead>
                  <tr>
                    {headerColumns}
                  </tr>
                </thead>
                <tbody>
                  {
                    this.state.loading ?
                      <tr><td colSpan={this.visibleColumns().length}><Spinner animation="border" variant="secondary" /></td></tr> :
                      this.state.data && this.state.data.Records.length > 0 ? dataRows(this.state.data) : (this.props.isSiteAdmin ? siteAdminEmptyRow : emptyRow)
                  }
                </tbody>
              </Table>
              {this.state.data && !this.state.loading && <React.Fragment>
                <span className="total-records">Total records: {this.state.data.Total}</span>
                <TablePagination pageNumber={this.state.pageNumber} pages={this.state.data.Pages} onChange={this.pageChanged} />
              </React.Fragment>}
            </Col>
          </Row>
        </Container>
        <UserInfo
          show={!!this.state.showUserInfoFor || this.state.showNewUser}
          mode={UserInfoMode.Team}
          onClose={this.handleInvitationSetupModalClose}
          LoginName={this.state.showUserInfoFor}
          LoginAttempts={this.state.loginAttempts}
          showNewUser={this.state.showNewUser}
          participationId={this.state.participationId}
          locationId={this.state.locationId}
          roleId={this.state.roleId}
          timeZoneId={this.state.timeZoneId} />
        {this.state.showUploadSpreadsheet &&
          <CSVUploadModal show={true}
            title="USER INVITE BULK UPLOAD"
            templateCSVUrl={ApiRootUrl() + "/Template/user-invite-template.csv"}
            onCancel={() => this.setState({ showUploadSpreadsheet: false })}
            onStartUpload={this.handleStartUploading}
            onUploaded={this.handleBulkUploaded}>
            {(this.props.isPortalAdmin || this.props.isCompanyAdmin) && <p>
              To send an invitation to multiple companies at once:
                <ol>
                  <li>
                    Create a Spreadsheet with the following columns:
                    <ol type="A">
                      <li>Company Name</li>
                      <li>User's First Name</li>
                      <li>User's Last Name</li>
                      <li>User's Email Address</li>
                      <li>User's Phone Number</li>
                      <li>User's Role</li>
                    </ol>
                  </li>
                  <li>Save the spreadsheet as a CSV text file.</li>
                  <li>Upload the file here.</li>
                </ol>
            </p>}
          </CSVUploadModal>}
    </AppPageWrapper>
    </>)
  }
}

function mapStateToProps(state: AppState): UserDetailsGridProps {
  return {
    isSiteAdmin: isSiteAdmin(state),
    isPortalAdmin: isPortalAdmin(state),
    isCompanyAdmin: isCompanyAdmin(state),
    UserName: getUserNameForSelectedCompany(state),
    PrimaryRoleID: RoleIdForSelectedCompany(state)
  }
}
const ConnectedUserDetailsGrid = connect(mapStateToProps, null)(UserDetailsGrid)
export default ConnectedUserDetailsGrid
